import { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  LayoutGrid,
  LogOut,
  MapPin,
  Newspaper,
  Search,
  UserRound,
  UserSquare2,
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import { redirectToLogin } from '@/lib/authUtils';
import { logoutAndRedirect } from '@/lib/logout';
import { normalizeImageUrl } from '@/lib/image';
import { cn } from '@/lib/utils';
import kalovateLogoLight from '@/assets/brand/kalovate-horizontal-full-color.svg';
import kalovateLogoDark from '@/assets/brand/kalovate-horizontal-reversed.svg';
import {
  PUBLIC_ROUTE_LAYOUTS,
  type PublicRouteKey,
  type FooterMode,
  type HeaderMode,
} from '@/lib/public-route-config';
import {
  getGeographyLinks,
  getPrimaryNavActiveKey,
  resolveShellGeographySelection,
} from '@/lib/public-navigation';
import { clearPersistedGeography } from '@/lib/geography';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import GeographyScopeBar from '@/components/geography-scope-bar';
import GeographySwitcher from '@/components/geography-switcher';
import { ThemeToggle } from '@/components/theme-toggle';
import type { AuthUser } from '@/hooks/useAuth';

interface PublicShellProps {
  routeKey: PublicRouteKey;
  children: React.ReactNode;
  stickyBar?: React.ReactNode;
  contentClassName?: string;
  surfaceClassName?: string;
}

function AccountAvatar({
  user,
  className,
  iconClassName = 'h-5 w-5',
}: {
  user: AuthUser | null | undefined;
  className?: string;
  iconClassName?: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const profileImageUrl = user?.profileImageUrl ?? null;
  const imageUrl = profileImageUrl && !imageFailed ? profileImageUrl : null;

  useEffect(() => {
    setImageFailed(false);
  }, [profileImageUrl]);

  if (imageUrl) {
    return (
      <img
        src={normalizeImageUrl(imageUrl, '')}
        alt=""
        className={cn('h-8 w-8 rounded-full object-cover', className)}
        onError={() => setImageFailed(true)}
        data-testid="account-avatar-image"
      />
    );
  }

  return <UserRound className={cn(iconClassName, className)} data-testid="account-avatar-icon" />;
}

function PublicHeader({ mode, routeKey }: { mode: HeaderMode; routeKey: PublicRouteKey }) {
  const [location] = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { regionSlug, localitySlug } = useGeographyContext();
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const resolvedGeography = resolveShellGeographySelection(location, search, {
    regionSlug,
    localitySlug,
  });
  const geographyLinks = getGeographyLinks(
    resolvedGeography.regionSlug,
    resolvedGeography.localitySlug
  );
  const activePrimaryNav = getPrimaryNavActiveKey(routeKey);
  const logoHref = routeKey === 'root' || routeKey === 'not-found' ? '/' : geographyLinks.basePath;
  const handleLogoClick = routeKey === 'not-found' ? () => clearPersistedGeography() : undefined;

  const { data: creatorApplication } = useQuery<{ status: string } | null>({
    queryKey: ['/api/creator/applications/current'],
    enabled: isAuthenticated && user?.role === 'user',
  });

  const showCreateWithKalovate =
    isAuthenticated && user?.role === 'user' && creatorApplication?.status !== 'pending';

  const handleAuthAction = async () => {
    if (isAuthenticated) {
      await logoutAndRedirect('/');
      return;
    }

    redirectToLogin();
  };

  if (mode === 'hidden') {
    return null;
  }

  if (mode === 'minimal') {
    return (
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-[calc(var(--shell-header-height)-0.5rem)] max-w-[var(--page-max-width)] items-center justify-between px-4 pt-[max(0px,var(--safe-area-top))] sm:px-6">
          <Link href={logoHref} onClick={handleLogoClick} className="flex items-center">
            <img src={kalovateLogoLight} alt="Kalovate" className="h-7 w-auto dark:hidden" />
            <img src={kalovateLogoDark} alt="Kalovate" className="hidden h-7 w-auto dark:block" />
          </Link>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {routeKey === 'checkout' ? 'Secure checkout' : 'Account flow'}
          </div>
        </div>
      </header>
    );
  }

  const primaryItems = [
    {
      key: 'calendar' as const,
      label: 'Calendar',
      href: geographyLinks.calendarHref,
      icon: CalendarDays,
    },
    {
      key: 'classes' as const,
      label: 'Classes',
      href: geographyLinks.classesHref,
      icon: LayoutGrid,
    },
    geographyLinks.locationsHref
      ? {
          key: 'locations' as const,
          label: 'Locations',
          href: geographyLinks.locationsHref,
          icon: MapPin,
        }
      : null,
    {
      key: 'creators' as const,
      label: 'Creators',
      href: geographyLinks.creatorsHref,
      icon: UserSquare2,
    },
    geographyLinks.updatesHref
      ? {
          key: 'updates' as const,
          label: 'Updates',
          href: geographyLinks.updatesHref,
          icon: Newspaper,
        }
      : null,
    {
      key: 'search' as const,
      label: 'Search',
      href: geographyLinks.searchHref,
      icon: Search,
    },
  ].filter(Boolean) as Array<{
    key: 'calendar' | 'classes' | 'locations' | 'creators' | 'updates' | 'search';
    label: string;
    href: string;
    icon: typeof CalendarDays;
  }>;

  const utilityItems = [
    user?.role === 'creator'
      ? {
          key: 'creator-dashboard' as const,
          label: 'Creator Dashboard',
          href: '/creator-dashboard',
        }
      : null,
    user?.role === 'venue'
      ? {
          key: 'venue-dashboard' as const,
          label: 'Venue Dashboard',
          href: '/venue-dashboard',
        }
      : null,
    // Ambassador is an additive capability — shows alongside the role dashboard.
    user?.role === 'ambassador' || user?.isAmbassador
      ? {
          key: 'ambassador-dashboard' as const,
          label: 'Ambassador Dashboard',
          href: '/ambassador-dashboard',
        }
      : null,
    user?.role === 'admin'
      ? { key: 'admin-dashboard' as const, label: 'Admin', href: '/admin' }
      : null,
    isAuthenticated
      ? { key: 'account-enrollments' as const, label: 'My Classes', href: '/account/enrollments' }
      : null,
    isAuthenticated ? { key: 'account' as const, label: 'Account', href: '/account' } : null,
    showCreateWithKalovate
      ? { key: 'creator-apply' as const, label: 'Create with Kalovate', href: '/creator/apply' }
      : null,
    // Guests get the entry point too — /creator/apply (BecomePartner) is public
    // and funnels them through sign-in/sign-up before the application.
    !isAuthenticated
      ? { key: 'creator-apply' as const, label: 'Create with Kalovate', href: '/creator/apply' }
      : null,
  ].filter(Boolean) as Array<{ key: PublicRouteKey; label: string; href: string }>;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/92 backdrop-blur-xl">
      <div className="mx-auto max-w-[var(--page-max-width)] px-4 pt-[max(0px,var(--safe-area-top))] sm:px-6">
        <div className="flex h-[var(--shell-header-height)] items-center justify-between gap-4 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:justify-normal">
          <div className="flex min-w-0 items-center gap-3 lg:pr-1 xl:pr-2">
            <Link href={logoHref} onClick={handleLogoClick} className="flex shrink-0 items-center">
              <img src={kalovateLogoLight} alt="Kalovate" className="h-8 w-auto dark:hidden" />
              <img src={kalovateLogoDark} alt="Kalovate" className="hidden h-8 w-auto dark:block" />
            </Link>
            <div className="hidden lg:block">
              <GeographySwitcher />
            </div>
          </div>

          <nav
            className="hidden min-w-0 items-center justify-center gap-1 lg:flex xl:gap-2"
            aria-label="Primary navigation"
          >
            {primaryItems.map(item => (
              <Link
                key={item.label}
                href={item.href}
                className={cn(
                  'touch-target inline-flex h-11 min-w-0 items-center justify-center whitespace-nowrap rounded-full px-3 text-center text-sm font-medium leading-none text-muted-foreground transition-colors hover:bg-muted hover:text-foreground xl:min-w-[6.25rem] xl:px-4',
                  activePrimaryNav === item.key &&
                    'bg-foreground text-background hover:bg-foreground hover:text-background'
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center justify-end gap-1 lg:flex lg:pl-1 xl:pl-2">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="touch-target rounded-full"
                    aria-label="Account menu"
                  >
                    <AccountAvatar user={user} className="h-8 w-8" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {utilityItems.map(item => (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link href={item.href} className={cn(routeKey === item.key && 'font-medium')}>
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => void handleAuthAction()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="touch-target rounded-full px-4"
                onClick={handleAuthAction}
              >
                Sign in
              </Button>
            )}
            <ThemeToggle />
          </div>

          <div className="flex items-center gap-1 lg:hidden">
            <Link
              href={geographyLinks.searchHref}
              aria-label="Search"
              className="touch-target inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Search className="h-5 w-5" />
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="touch-target rounded-full"
                  aria-label={isAuthenticated ? 'Account menu' : 'Menu'}
                >
                  {isAuthenticated ? (
                    <AccountAvatar user={user} className="h-8 w-8" />
                  ) : (
                    <UserRound className="h-5 w-5" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {utilityItems.map(item => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link href={item.href} className={cn(routeKey === item.key && 'font-medium')}>
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                {utilityItems.length > 0 && <DropdownMenuSeparator />}
                {/* Non-item row so toggling the theme doesn't close the menu. */}
                <div className="flex items-center justify-between px-2 py-1.5 text-sm">
                  <span className="text-muted-foreground">Appearance</span>
                  <ThemeToggle />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleAuthAction()}>
                  {isAuthenticated ? (
                    <>
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </>
                  ) : (
                    <>
                      <UserRound className="mr-2 h-4 w-4" />
                      Sign in
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}

function PublicBottomNav({ routeKey }: { routeKey: PublicRouteKey }) {
  const [location] = useLocation();
  const { regionSlug, localitySlug } = useGeographyContext();
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const resolvedGeography = resolveShellGeographySelection(location, search, {
    regionSlug,
    localitySlug,
  });
  const geographyLinks = getGeographyLinks(
    resolvedGeography.regionSlug,
    resolvedGeography.localitySlug
  );
  const activePrimaryNav = getPrimaryNavActiveKey(routeKey);

  const items = [
    {
      key: 'calendar' as const,
      label: 'Calendar',
      href: geographyLinks.calendarHref,
      icon: CalendarDays,
    },
    geographyLinks.updatesHref
      ? {
          key: 'updates' as const,
          label: 'Updates',
          href: geographyLinks.updatesHref,
          icon: Newspaper,
        }
      : null,
    geographyLinks.locationsHref
      ? {
          key: 'locations' as const,
          label: 'Locations',
          href: geographyLinks.locationsHref,
          icon: MapPin,
        }
      : null,
    {
      key: 'creators' as const,
      label: 'Creators',
      href: geographyLinks.creatorsHref,
      icon: UserSquare2,
    },
    {
      key: 'classes' as const,
      label: 'Classes',
      href: geographyLinks.classesHref,
      icon: LayoutGrid,
    },
  ].filter(Boolean) as Array<{
    key: 'calendar' | 'updates' | 'locations' | 'creators' | 'classes';
    label: string;
    href: string;
    icon: typeof CalendarDays;
  }>;

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border/70 bg-background/96 backdrop-blur lg:hidden"
      data-testid="bottom-nav"
    >
      <div
        className={cn(
          'grid h-[calc(var(--shell-bottom-nav-height)+var(--safe-area-bottom))] px-2 pb-[max(0px,var(--safe-area-bottom))]',
          items.length >= 5 ? 'grid-cols-5' : items.length === 4 ? 'grid-cols-4' : 'grid-cols-3'
        )}
      >
        {items.map(item => {
          const active = activePrimaryNav === item.key;

          return (
            <Link
              key={item.label}
              href={item.href}
              className={cn(
                'touch-target flex flex-col items-center justify-center gap-1 rounded-2xl text-[0.7rem] font-medium text-muted-foreground transition-colors',
                active && 'text-foreground'
              )}
            >
              <item.icon className={cn('h-4 w-4', active && 'scale-110')} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function PublicFooter({ mode }: { mode: FooterMode }) {
  const { regionSlug, localitySlug } = useGeographyContext();
  const geographyLinks = useMemo(
    () => getGeographyLinks(regionSlug, localitySlug),
    [localitySlug, regionSlug]
  );

  if (mode === 'none') {
    return null;
  }

  if (mode === 'minimal') {
    return (
      <footer className="border-t border-border/60 bg-background/70">
        <div className="mx-auto flex max-w-[var(--page-max-width)] flex-col gap-2 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>Kalovate helps people learn locally and online from independent creators.</p>
          <div className="flex gap-4">
            <Link href="/contact">Support</Link>
            <Link href="/faq">FAQ</Link>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-border/60 bg-[linear-gradient(180deg,rgba(255,255,255,0),rgba(139,69,19,0.04))]">
      <div className="mx-auto max-w-[var(--page-max-width)] px-4 py-12 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_repeat(3,minmax(0,1fr))]">
          <div className="space-y-4">
            <img src={kalovateLogoLight} alt="Kalovate" className="h-8 w-auto dark:hidden" />
            <img src={kalovateLogoDark} alt="Kalovate" className="hidden h-8 w-auto dark:block" />
            <p className="max-w-sm text-sm leading-6 text-muted-foreground">
              Local classes, small-group learning, and creator-led experiences designed for the
              rhythm of real neighborhoods.
            </p>
            <div className="flex gap-3 text-sm text-muted-foreground">
              <span className="rounded-full border border-border/70 px-3 py-1">Hoosier Hills</span>
              <span className="rounded-full border border-border/70 px-3 py-1">
                Local discovery
              </span>
              <span className="rounded-full border border-border/70 px-3 py-1">Creator-led</span>
            </div>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Discover
            </p>
            <div className="grid gap-3 text-sm">
              <Link href={geographyLinks.calendarHref}>Calendar</Link>
              <Link href={geographyLinks.classesHref}>Classes</Link>
              <Link href={geographyLinks.creatorsHref}>Creators</Link>
              <Link href={geographyLinks.searchHref}>Search</Link>
            </div>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Platform
            </p>
            <div className="grid gap-3 text-sm">
              <Link href="/creator/apply">Create with Kalovate</Link>
              <Link href="/faq">FAQ</Link>
              <Link href="/about">About</Link>
              <Link href="/contact">Contact</Link>
            </div>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Legal
            </p>
            <div className="grid gap-3 text-sm">
              <a href="/privacy" className="hover:text-foreground">
                Privacy Policy
              </a>
              <a href="/terms" className="hover:text-foreground">
                Terms of Service
              </a>
              <a href="/terms#community" className="hover:text-foreground">
                Community Guidelines
              </a>
            </div>
          </div>
        </div>
        <div className="mt-10 border-t border-border/40 pt-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Kalovate. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

function shouldShowGeographyScopeBar(routeKey: PublicRouteKey, pathname: string) {
  if (
    routeKey === 'region-home' ||
    routeKey === 'region-calendar' ||
    routeKey === 'region-community' ||
    routeKey === 'locality-home' ||
    routeKey === 'locality-calendar' ||
    routeKey === 'locality-community' ||
    routeKey === 'search' ||
    routeKey === 'updates' ||
    routeKey === 'locations'
  ) {
    return true;
  }

  if ((routeKey === 'classes' || routeKey === 'creators') && pathname.startsWith('/regions/')) {
    return true;
  }

  return false;
}

export function PublicShell({
  routeKey,
  children,
  stickyBar,
  contentClassName,
  surfaceClassName,
}: PublicShellProps) {
  const layout = PUBLIC_ROUTE_LAYOUTS[routeKey];
  const showBottomNav = layout.bottomNavVisible;
  const [location] = useLocation();
  const showGeographyScopeBar = shouldShowGeographyScopeBar(routeKey, location);

  return (
    <div className={cn('min-h-[100dvh] bg-background text-foreground', surfaceClassName)}>
      <PublicHeader mode={layout.headerMode} routeKey={routeKey} />
      <div
        className={cn(
          'flex min-h-[100dvh] flex-col',
          layout.headerMode !== 'hidden' &&
            'pt-[calc(var(--shell-header-height)+var(--safe-area-top))]'
        )}
      >
        {showGeographyScopeBar ? (
          <div className="sticky top-[calc(var(--shell-header-height)+var(--safe-area-top))] z-40 [@media(max-height:600px)]:static">
            <GeographyScopeBar />
          </div>
        ) : null}
        <main
          className={cn(
            'flex-1 pb-8',
            showBottomNav &&
              'pb-[calc(var(--shell-bottom-nav-height)+var(--safe-area-bottom)+1.5rem)]',
            stickyBar && 'pb-[calc(var(--shell-bottom-nav-height)+var(--safe-area-bottom)+6rem)]',
            contentClassName
          )}
        >
          {children}
        </main>
        <PublicFooter mode={layout.footer} />
      </div>
      {showBottomNav ? <PublicBottomNav routeKey={routeKey} /> : null}
      {stickyBar ? (
        <div
          className={cn(
            'fixed inset-x-0 z-[51] bg-background/96 px-4 pt-3 backdrop-blur lg:hidden',
            showBottomNav
              ? 'bottom-[calc(var(--shell-bottom-nav-height)+var(--safe-area-bottom))] pb-3'
              : 'bottom-0 pb-[calc(var(--safe-area-bottom)+0.75rem)]'
          )}
        >
          <div className="mx-auto max-w-[var(--page-max-width)]">{stickyBar}</div>
        </div>
      ) : null}
    </div>
  );
}
